import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ClipboardCheck, FileDown } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { downloadChecklistPdf, KIND_LABELS } from "@/components/ChecklistDialog";
import { ChecklistTemplateEditor } from "@/components/ChecklistTemplateEditor";
import { ChecklistStats } from "@/components/ChecklistStats";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { ChecklistExpiries } from "@/components/ChecklistExpiries";
import { ChecklistTemperatures } from "@/components/ChecklistTemperatures";
import { ChecklistKm } from "@/components/ChecklistKm";

function History({ kind }) {
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ vehicle_id: "", date_from: "", date_to: "" });
  const [list, setList] = useState([]);
  const [tick, setTick] = useState(0);
  useAutoRefresh(() => setTick((t) => t + 1), 30000);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = { kind, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
    api.get("/checklists", { params }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [filters, kind, tick]);
  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3 rounded-xl border border-border bg-card p-4" data-testid="checklist-filters">
        <div className="space-y-1">
          <Label>Ambulanza</Label>
          <Select value={filters.vehicle_id || "all"} onValueChange={(v) => set("vehicle_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-vehicle"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label>Dal</Label><Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} /></div>
        <div className="space-y-1"><Label>Al</Label><Input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} /></div>
        <div className="flex items-end"><Button variant="ghost" data-testid="filter-reset" onClick={() => setFilters({ vehicle_id: "", date_from: "", date_to: "" })}>Azzera filtri</Button></div>
      </div>
      <p className="text-sm text-muted-foreground" data-testid="checklist-count">{list.length} check-list trovate</p>
      {list.map((c) => (
        <div key={c.id} data-testid={`history-item-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-3">
          <div className="min-w-0">
            <p className="font-semibold">{c.service_name}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(c.signed_at).toLocaleString("it-IT")} · {c.compiled_by_name}{c.extra_operators ? `, ${c.extra_operators}` : ""}{c.vehicle_name ? ` · ${c.vehicle_name}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={c.missing_count ? "text-red-700 border-red-200" : "text-emerald-700 border-emerald-200"}>
              {c.missing_count ? `${c.missing_count} mancanti` : "Completa"}
            </Badge>
            <Button size="sm" variant="outline" onClick={() => downloadChecklistPdf(c)} data-testid={`history-pdf-${c.id}`} className="rounded-full"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ChecklistPage() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [qs] = useSearchParams();
  const isCoord = user.role === "coordinatore";
  const [kind, setKind] = useState(qs.get("kind") === "zaino" ? "zaino" : "vano");
  useEffect(() => { if (!isCoord) navigate("/"); }, [isCoord, navigate]);
  if (!isCoord) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" data-testid="checklist-back" onClick={() => navigate("/")} className="rounded-full"><ArrowLeft className="h-5 w-5" /></Button>
          <BrandLogo logoUrl={settings.logo_url} size={38} />
          <div className="leading-tight">
            <p className="font-display font-bold text-sm sm:text-base flex items-center gap-1.5"><ClipboardCheck className="h-4 w-4" /> Check-list</p>
            <p className="text-xs text-muted-foreground hidden sm:block">{settings.committee_name}</p>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
        <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="kind-switch">
          {Object.entries(KIND_LABELS).map(([k, label]) => (
            <button key={k} type="button" data-testid={`kind-${k}`} onClick={() => setKind(k)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${kind === k ? "bg-[#D32F2F] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
        <Tabs defaultValue={qs.get("tab") || "storico"}>
          <TabsList className="max-w-full overflow-x-auto justify-start">
            <TabsTrigger value="storico" data-testid="tab-storico">Storico</TabsTrigger>
            <TabsTrigger value="riepilogo" data-testid="tab-riepilogo">Riepilogo materiali</TabsTrigger>
            <TabsTrigger value="scadenze" data-testid="tab-scadenze">Scadenze</TabsTrigger>
            {kind === "zaino" && <TabsTrigger value="temperature" data-testid="tab-temperature">Temperature</TabsTrigger>}
            {kind === "mezzo" && <TabsTrigger value="km" data-testid="tab-km">Km e carburante</TabsTrigger>}
            <TabsTrigger value="voci" data-testid="tab-voci">Voci check-list</TabsTrigger>
          </TabsList>
          <TabsContent value="storico" className="mt-6"><History kind={kind} /></TabsContent>
          <TabsContent value="riepilogo" className="mt-6"><ChecklistStats key={kind} kind={kind} initialFrom={qs.get("from")} initialTo={qs.get("to")} /></TabsContent>
          <TabsContent value="scadenze" className="mt-6"><ChecklistExpiries kind={kind} /></TabsContent>
          {kind === "zaino" && <TabsContent value="temperature" className="mt-6"><ChecklistTemperatures /></TabsContent>}
          {kind === "mezzo" && <TabsContent value="km" className="mt-6"><ChecklistKm /></TabsContent>}
          <TabsContent value="voci" className="mt-6"><ChecklistTemplateEditor key={kind} kind={kind} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
